import React, {useMemo} from "react";
import {useFormikContext} from "formik";
import {useSelector} from "react-redux";
import {Text, styled} from "@reside/ui";

import {AppState, select} from "../../../../store";
import {FacilitySettingsVariables} from "../../../../services/FacilitySettingsService";
import {useTemplateVersionQuery} from "../../../../hooks/useTemplateVersionQuery";
import {useWorkflowFacilitySettingsTemplateQuery} from "../../../../hooks/useTemplateQuery";
import {useFacilitySettingsParams} from "../../hooks/useFacilitySettingsParams";
import {
  extractWorkflowSettingsBlocks,
  getWorkflowNavItems,
} from "./workflowSettingsBlocks";

type Props = Readonly<{
  workflowType: string;
  workflowLabel: string;
  activeId: string;
  onSelectItem: (itemId: string) => void;
}>;

export const WorkflowSideNavSubItems = ({
  workflowType,
  workflowLabel,
  activeId,
  onSelectItem,
}: Props) => {
  const {facilityId} = useFacilitySettingsParams();
  const {values} = useFormikContext<FacilitySettingsVariables>();
  const isResideAdmin = useSelector((state: AppState) =>
    select.adminSession.isResideAdmin(state),
  );
  const {data: templateVersionData} = useTemplateVersionQuery({facilityId});
  const versionId = templateVersionData?.version?.id;

  const {data: workflowTemplate} = useWorkflowFacilitySettingsTemplateQuery({
    versionId,
    workflowType,
  });

  const mergedAnswers = useMemo(
    () => ({
      ...values,
      ...(values.workflows?.[workflowType] ?? {}),
    }),
    [values, workflowType],
  );

  const workflowBlocks = useMemo(
    () =>
      extractWorkflowSettingsBlocks(
        workflowTemplate,
        mergedAnswers,
        isResideAdmin,
      ),
    [workflowTemplate, mergedAnswers, isResideAdmin],
  );

  const navItems = useMemo(
    () =>
      getWorkflowNavItems({
        workflowType,
        workflowLabel,
        workflowBlocks,
        answers: mergedAnswers,
        isResideAdmin,
      }),
    [workflowType, workflowLabel, workflowBlocks, mergedAnswers, isResideAdmin],
  );

  return (
    <>
      {navItems.map(item => (
        <SidebarSubItem
          key={item.id}
          type="button"
          $active={activeId === item.id}
          onClick={() => onSelectItem(item.id)}
        >
          <Text
            fontWeight={activeId === item.id ? "bold" : "normal"}
            lineHeight="18px"
            color={activeId === item.id ? "primary" : "darkBlue100"}
          >
            {item.label}
          </Text>
        </SidebarSubItem>
      ))}
    </>
  );
};

const SidebarSubItem = styled.button<{$active: boolean}>`
  display: block;
  width: 100%;
  border: none;
  padding: 5px 15px 5px 27px;
  border-radius: 4px;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  font-size: inherit;
  background: ${({$active, theme}) =>
    $active ? `${theme.color.primary}12` : "transparent"};

  &:hover {
    opacity: ${({$active}) => ($active ? 1 : 0.5)};
  }
`;
